import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import { DatatableController, DatatableViewStateSnapshot } from '../shared/datatable.controller';
import { formatCompactOrIsoDate, formatCurrencyAmount, toDateSortValue } from '../shared/trade-formatters';
import { SwapRow } from './swaps.types';
import { SwapsService } from './swaps.service';

@Component({
  selector: 'app-swaps',
  imports: [CommonModule, RouterLink],
  templateUrl: './swaps.html',
  styleUrl: './swaps.css',
})
export class Swaps implements OnInit, OnDestroy {
  protected readonly datatableId = 'kt_swaps_datatable';
  private readonly datatableController = new DatatableController(this.datatableId);
  private requestSubscription?: Subscription;

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<SwapRow[]>([]);

  constructor(private readonly swapsService: SwapsService) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  protected onRetry(): void {
    const snapshot = this.datatableController.captureViewState();
    this.loadRows(snapshot);
  }

  protected trackByRow(index: number, row: SwapRow): string {
    return `${row.dealId}-${row.tradeId}-${index}`;
  }

  protected formatDate(value: string): string {
    return formatCompactOrIsoDate(value);
  }

  protected formatNotional(currency: string, notional: number): string {
    return formatCurrencyAmount(currency, notional);
  }

  protected dateSortValue(value: string): number | null {
    return toDateSortValue(value);
  }

  protected amountSortValue(value: number): number | null {
    return Number.isFinite(value) ? value : null;
  }

  private loadRows(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.requestSubscription?.unsubscribe();

    this.requestSubscription = this.swapsService.getSwaps().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.rows.set([]);
        this.errorMessage.set('Unable to load swap data from the API.');
        this.isLoading.set(false);
      },
    });
  }
}